import { getAuth } from 'firebase/auth';
import * as THREE from 'three';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { Viewer } from './ttViewer';
import { ttMetashapeCamera, ttMetashapeSensor } from './ttMetashapeParse';
import { Polyline } from './ttObject';
import router from '../router';
import { doc, getDoc, type Firestore } from '@firebase/firestore';

export function contrastColor(color: number): string {
    const r = (color >> 16) & 255;
    const g = (color >> 8) & 255;
    const b = color & 255;
    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return (yiq >= 32) ? '#000000' : '#DDDDDD';
}

export function clamp(num: number, min: number, max: number): number {
    return Math.min(Math.max(num, min), max);
}

export function toNormalizedScreenCoord(x: number, y: number, canvas: HTMLElement): THREE.Vector2 {
    const mouse = new THREE.Vector2();
    const rect = canvas.getBoundingClientRect();
    mouse.x =  ((x - rect.left) / canvas.clientWidth ) * 2 - 1;
    mouse.y = -((y - rect.top ) / canvas.clientHeight) * 2 + 1;
    return mouse;
}

export function raycastPointOnMainModel(viewer: Viewer, event: MouseEvent): THREE.Intersection | null {
    if (!viewer.canvas)
        return null;
    const mouse = toNormalizedScreenCoord(event.clientX, event.clientY, viewer.canvas);
    viewer.raycaster.setFromCamera(mouse, viewer.camera);
    const intersects = viewer.raycaster.intersectObjects(viewer.models, true);
    if (intersects.length > 0) {
        return intersects[0];
    }
    return null;
}

export function raycastPointOnImage(viewer: Viewer, event: MouseEvent): THREE.Intersection | null {
    if (!viewer.imageCanvas)
        return null;
    const mouse = toNormalizedScreenCoord(event.clientX, event.clientY, viewer.imageCanvas);
    viewer.raycaster.setFromCamera(mouse, viewer.orthoCamera);
    const intersects = viewer.raycaster.intersectObjects([viewer.imageMesh], true);
    if (intersects.length === 0)
        return null;
    // The image plane always occupies [0..width, 0..height]. Pan and zoom only
    // affect the orthographic camera, so the hit remains a stable plane-space
    // coordinate regardless of the window size or viewport state.
    intersects[0].point.z = 0;
    return intersects[0];
}

/** Return the dimensions after the camera's EXIF/XML orientation is applied. */
export function orientedImageSize(
    sensor: ttMetashapeSensor,
    orientation: number,
    target = new THREE.Vector2(),
): THREE.Vector2 {
    if (orientation >= 5 && orientation <= 8) {
        return target.set(sensor.heightPx, sensor.widthPx);
    }
    return target.set(sensor.widthPx, sensor.heightPx);
}

/** Convert calibrated raw pixels to the browser's EXIF-oriented image plane. */
export function imagePixelToPlanePoint(
    sensor: ttMetashapeSensor,
    imageX: number,
    imageY: number,
    orientation: number,
    target = new THREE.Vector2(),
): THREE.Vector2 {
    const width = sensor.widthPx;
    const height = sensor.heightPx;
    const size = orientedImageSize(sensor, orientation);
    let displayX = imageX;
    let displayY = imageY;

    switch (orientation) {
        case 2: displayX = width - imageX; break;
        case 3: displayX = width - imageX; displayY = height - imageY; break;
        case 4: displayY = height - imageY; break;
        case 5: displayX = imageY; displayY = imageX; break;
        case 6: displayX = height - imageY; displayY = imageX; break;
        case 7: displayX = height - imageY; displayY = width - imageX; break;
        case 8: displayX = imageY; displayY = width - imageX; break;
    }
    return target.set(displayX, size.y - displayY);
}

/** Convert an EXIF-oriented image-plane point back to calibrated raw pixels. */
export function imagePlanePointToPixel(
    sensor: ttMetashapeSensor,
    planeX: number,
    planeY: number,
    orientation: number,
    target = new THREE.Vector2(),
): THREE.Vector2 {
    const displayX = planeX;
    const displayY = orientedImageSize(sensor, orientation).y - planeY;
    const width = sensor.widthPx;
    const height = sensor.heightPx;

    switch (orientation) {
        case 2: return target.set(width - displayX, displayY);
        case 3: return target.set(width - displayX, height - displayY);
        case 4: return target.set(displayX, height - displayY);
        case 5: return target.set(displayY, displayX);
        case 6: return target.set(displayY, height - displayX);
        case 7: return target.set(width - displayY, height - displayX);
        case 8: return target.set(width - displayY, displayX);
        default: return target.set(displayX, displayY);
    }
}

/** Project a Metashape-local camera point to top-left-origin image pixels. */
export function projectCameraPointToImagePixel(
    point: THREE.Vector3,
    sensor: ttMetashapeSensor,
    target = new THREE.Vector2(),
): THREE.Vector2 | null {
    if (!Number.isFinite(point.z) || point.z <= 0 || !Number.isFinite(sensor.f)) return null;
    const x = point.x / point.z;
    const y = point.y / point.z;
    const r2 = x * x + y * y;
    const radial = 1
        + sensor.k1 * r2
        + sensor.k2 * r2 * r2
        + sensor.k3 * r2 * r2 * r2
        + sensor.k4 * r2 * r2 * r2 * r2;
    const xp = x * radial + sensor.p1 * (r2 + 2 * x * x) + 2 * sensor.p2 * x * y;
    const yp = y * radial + sensor.p2 * (r2 + 2 * y * y) + 2 * sensor.p1 * x * y;
    return target.set(
        sensor.widthPx * 0.5 + sensor.cx + xp * sensor.f,
        sensor.heightPx * 0.5 + sensor.cy + yp * sensor.f,
    );
}

export function projectLineOnImage(
    line: Polyline,
    tfx: THREE.Matrix4,
    sensor: ttMetashapeSensor,
    orientation: number,
): LineGeometry|null {
    const point = new THREE.Vector3();
    const imagePoint = new THREE.Vector2();
    const planePoint = new THREE.Vector2();
    const visibleRuns: number[][] = [];
    let currentRun: number[] = [];
    for (let i = 0; i < line.count(); i++) {
        line.vertexAt(i, point);
        point.applyMatrix4(tfx);
        const projected = projectCameraPointToImagePixel(point, sensor, imagePoint);
        if (!projected) {
            if (currentRun.length >= 6) visibleRuns.push(currentRun);
            currentRun = [];
            continue;
        }
        imagePixelToPlanePoint(sensor, projected.x, projected.y, orientation, planePoint);
        currentRun.push(planePoint.x, planePoint.y, 0.1);
    }
    if (currentRun.length >= 6) visibleRuns.push(currentRun);
    if (visibleRuns.length === 0) return null;
    // LineGeometry cannot represent disconnected runs without joining them;
    // keep the longest visible portion when a curve crosses behind the camera.
    const unprojectedPointsData = visibleRuns.reduce((longest, run) => run.length > longest.length ? run : longest);
    const geometry = new LineGeometry();
    geometry.setPositions(unprojectedPointsData);
    return geometry;
}

/** Convert a pixel in Metashape's image coordinate system back to an undistorted camera ray. */
function undistortImagePoint(sensor: ttMetashapeSensor, imageX: number, imageY: number): THREE.Vector2 | null {
    if (!Number.isFinite(sensor.f) || Math.abs(sensor.f) < Number.EPSILON) return null;

    // This is the exact inverse coordinate convention of projectLineOnImage().
    const distortedX = (imageX - sensor.widthPx * 0.5 - sensor.cx) / sensor.f;
    const distortedY = (imageY - sensor.heightPx * 0.5 - sensor.cy) / sensor.f;
    let x = distortedX;
    let y = distortedY;

    // Metashape stores Brown-Conrady lens parameters. Fixed-point iteration is
    // stable for the calibrated tunnel cameras and avoids drawing offsets away
    // from the image centre on wide-angle captures.
    for (let iteration = 0; iteration < 8; iteration++) {
        const r2 = x * x + y * y;
        const radial = 1
            + sensor.k1 * r2
            + sensor.k2 * r2 * r2
            + sensor.k3 * r2 * r2 * r2
            + sensor.k4 * r2 * r2 * r2 * r2;
        if (!Number.isFinite(radial) || Math.abs(radial) < Number.EPSILON) return null;
        const tangentialX = sensor.p1 * (r2 + 2 * x * x) + 2 * sensor.p2 * x * y;
        const tangentialY = sensor.p2 * (r2 + 2 * y * y) + 2 * sensor.p1 * x * y;
        x = (distortedX - tangentialX) / radial;
        y = (distortedY - tangentialY) / radial;
    }
    return Number.isFinite(x) && Number.isFinite(y) ? new THREE.Vector2(x, y) : null;
}

/** Convert top-left-origin image pixels to a Metashape-local camera ray. */
export function imagePixelToCameraRay(sensor: ttMetashapeSensor, imageX: number, imageY: number): THREE.Vector3 | null {
    const normalizedPoint = undistortImagePoint(sensor, imageX, imageY);
    return normalizedPoint ? new THREE.Vector3(normalizedPoint.x, normalizedPoint.y, 1) : null;
}

export function unprojectImagePoint(
    camera: ttMetashapeCamera,
    sensor: ttMetashapeSensor,
    models: THREE.Object3D[],
    imageX: number,
    imageY: number,
): THREE.Vector3|null {
    const cameraToWorld = camera.worldToCamera.clone().invert();
    const imagePixel = imagePlanePointToPixel(
        sensor,
        imageX,
        imageY,
        camera.orientation,
    );
    const cameraRay = imagePixelToCameraRay(sensor, imagePixel.x, imagePixel.y);
    if (!cameraRay) return null;
    const pos = new THREE.Vector3();
    const point = cameraRay;
    pos.setFromMatrixPosition(cameraToWorld);
    point.applyMatrix4(cameraToWorld);
    const dir = new THREE.Vector3();
    dir.subVectors(point, pos);

    const raycaster = new THREE.Raycaster()
    raycaster.set(pos, dir);
    const intersects = raycaster.intersectObjects(models, true);
    if (intersects.length === 0)
        return null;
    return intersects[0].point;
}

export async function onLogout() {
    const auth = getAuth();
    auth.signOut().then(() => {
        console.log("Sign Out completed");
        router.push("/");
    })
    .catch((error) => console.log(error));
};

// Only emails listed in config/admins may assign users to projects.
export async function isAdminEmail(db: Firestore, email: string): Promise<boolean> {
    const snapshot = await getDoc(doc(db, 'config', 'admins'));
    const emails = snapshot.data()?.emails;
    return Array.isArray(emails) && emails.includes(email.toLowerCase());
}

export async function onProfile() {
    router.push("/profile");
};

export async function onCreateProject() {
    router.push("/createproject");
};

export function extractVerticesFromLineGeometry2dArray(geometry: LineGeometry): number[] {
    const instanceStart = geometry.attributes.instanceStart;
    const instanceEnd = geometry.attributes.instanceEnd;
    if (instanceStart === null || instanceEnd === null)
        return [];
    const vertex = new THREE.Vector3();
    let data: number[] = [];
    for (let i = 0; i < instanceStart.count; i++) {
        vertex.fromBufferAttribute(instanceStart, i);
        data.push(vertex.x);
        data.push(vertex.y);
    }
    vertex.fromBufferAttribute(instanceEnd, instanceEnd.count - 1);
    data.push(vertex.x);
    data.push(vertex.y);
    return data;
}

export function extractVerticesFromLineGeometry3dArray(geometry: LineGeometry): number[] {
    const positions = geometry.attributes.position.array;
    const vertices: number[] = [];
    for (let i = 0; i < positions.length; i += 3) {
        const x = positions[i];
        const y = positions[i + 1];
        const z = positions[i + 2];
        vertices.push(x);
        vertices.push(y);
        vertices.push(z);
    }
    return vertices;
}

export function extractVerticesFromLineGeometry2d(geometry: LineGeometry): THREE.Vector2[] {
    const positions = geometry.attributes.position.array;
    const vertices: THREE.Vector2[] = [];
    for (let i = 0; i < positions.length; i += 3) {
        const x = positions[i];
        const y = positions[i + 1];
        vertices.push(new THREE.Vector2(x, y));
    }
    return vertices;
}

export function extractVerticesFromLineGeometry(geometry: LineGeometry): THREE.Vector3[] {
    const positions = geometry.attributes.position.array;
    const vertices: THREE.Vector3[] = [];
    for (let i = 0; i < positions.length; i += 3) {
        const x = positions[i];
        const y = positions[i + 1];
        const z = positions[i + 2];
        vertices.push(new THREE.Vector3(x, y, z));
    }
    return vertices;
}

export function severityToStr(severity: any) : string {
    if (typeof severity === "string") {
        severity = severity.toLowerCase();
        if (severity === "unknown") {
            return severity;
        }
        else if (severity === "low") {
            return severity;
        }
        else if (severity === "normal" || severity === "medium") {
            return severity;
        }
        else if (severity === "high") {
            return severity;
        }
        else {
            console.error("Unknown type of severity: ", severity);
            return "unknown";
        }
    }
    else if (typeof severity === "number") {
        switch (severity) {
            case 0: return "unknown";
            case 1: return "low";
            case 2: return "medium";
            case 3: return "high";
            default: {
                console.error("Unknown severity nubmer");
                return "unknown";
            }
        }
    }
    else {
        console.error("Unknown type of severity: ", severity);
        return "unknown";
    }
}

export function severityToNum(severity: any) : number {
    if (severity) {
        if (typeof severity === "string") {
            severity = severity.toLowerCase();
            if (severity === "unknown") {
                return 0;
            }
            else if (severity === "low") {
                return 1;
            }
            else if (severity === "normal" || severity === "medium") {
                return 2;
            }
            else if (severity === "high") {
                return 3;
            }
            else if (parseInt(severity)){
                return parseInt(severity);
            }
            else {
                console.error("Unknown type of severity: ", severity);
            }
        }
        else if (typeof severity === "number") {
            return severity;
        }
        else {
            console.error("Unknown type of severity: ", severity);
        }
    }
    return 0;
}

export function severityNumToStr(severity: number) : string {
    switch (severity) {
        case 0: return "unknown";
        case 1: return "low";
        case 2: return "medium";
        case 3: return "high";
        default: {
            console.error("Unknown severity nubmer");
            return "unknown";
        }
    }
}

export function severityStrToNum(severity: string) : number {
    if (typeof severity === "string") {
        severity = severity.toLowerCase();
        if (severity === "unknown") {
            return 0;
        }
        else if (severity === "low") {
            return 1;
        }
        else if (severity === "normal" || severity === "medium") {
            return 2;
        }
        else if (severity === "high") {
            return 3;
        }
        else {
            console.error("Unknown type of severity: ", severity);
            return 0;
        }
    }
    else if (typeof severity === "number") {
        return severity;
    }
    else {
        console.error("Unknown type of severity: ", severity);
        return 0;
    }
}

export function openJsonInNewTab(data: any) {
    const jsonStr = JSON.stringify(data, null, 2); // Pretty-printed
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 10000);
}
